import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CreateDraftDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    projectId: string
    parentDraftId?: string
    parentDraftName?: string
    onSuccess?: (draftId: string) => void
}

export function CreateDraftDialog({
    open,
    onOpenChange,
    projectId,
    parentDraftId,
    parentDraftName,
    onSuccess
}: CreateDraftDialogProps) {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (data: { name: string; description: string; parentDraftId?: string }) =>
            api.post<{ id: string }>(`/projects/${projectId}/drafts`, data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['drafts', projectId] })
            toast({ title: 'Draft created', description: 'New draft has been successfully created.' })
            onOpenChange(false)
            setName('')
            setDescription('')
            if (response.data?.id) {
                onSuccess?.(response.data.id)
            }
        },
        onError: (error: any) => {
            toast({
                title: 'Error creating draft',
                description: error.message || 'Something went wrong',
                variant: 'destructive'
            })
        }
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        mutation.mutate({ name, description, parentDraftId })
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create New Draft</DialogTitle>
                    <DialogDescription>
                        {parentDraftId
                            ? `Create a new draft branched from "${parentDraftName || 'current draft'}".`
                            : "Create a new blank draft."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Draft Name</Label>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Revision 2"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="description">Description (optional)</Label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Notes about changes in this draft..."
                            className={cn(
                                "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            )}
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={mutation.isPending}>
                            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Create Draft
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
